import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Empty, Input, Select, Spin, Switch, Tag, Typography } from 'antd';
import {
  ApartmentOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  GlobalOutlined,
  HddOutlined,
  ReloadOutlined,
  SearchOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import './MapaInfraestructura.css';

const { Text } = Typography;

const PROVIDER_COLORS = {
  CONTABO: '#0168fa',
  'SSD NODES': '#00b8d4',
  HETZNER: '#dc3545',
  PROPIO: '#10b759',
  'LOCAL': '#8392a5',
  'CONTABO VPS - AMD EPYC': '#a855f7',
  'LOCAL - AMD RYZEN 5 5500': '#8392a5',
  CLOUDFLARE: '#fd7e14',
};

const ROOT_COLORS = ['#0168fa', '#00b8d4', '#fd7e14', '#10b759', '#f10075', '#3b82f6', '#fd7e14', '#00cccc', '#042893', '#dc3545'];

const BACKUP_LABELS = {
  12: 'Backups locales',
  21: 'Failover web / IA',
  22: 'Réplica DB ESM',
  26: 'Backup correo',
};

const RELATIONS = [
  { from: 14, to: 21, type: 'failover', label: 'sync failover cada 10 min' },
  { from: 5, to: 21, type: 'failover', label: 'sync fallback 208 cada 10 min' },
  { from: 6, to: 22, type: 'replica', label: 'réplica MySQL ESM' },
  { from: 20, to: 26, type: 'backup', label: 'respaldo de correo' },
];

const SECOND_LEVEL = new Set(['com.co', 'gov.co', 'edu.co', 'org.co', 'net.co', 'com.ec', 'com.mx', 'com.ar', 'com.pe', 'com.ve', 'com.br', 'co.uk', 'org.uk']);

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeDomain = (value) => String(value || '')
  .toLowerCase()
  .trim()
  .replace(/^https?:\/\//, '')
  .replace(/\/.*$/, '')
  .replace(/\.$/, '');

const parseList = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const rootOf = (domain, knownRoots) => {
  const normalized = normalizeDomain(domain);
  if (!normalized) return '';
  const known = knownRoots.find((root) => normalized === root || normalized.endsWith(`.${root}`));
  if (known) return known;
  const parts = normalized.split('.');
  if (parts.length <= 2) return normalized;
  const lastTwo = parts.slice(-2).join('.');
  if (SECOND_LEVEL.has(lastTwo) && parts.length >= 3) return parts.slice(-3).join('.');
  return lastTwo;
};

const subdomainLabel = (full, root) => {
  if (!full || full === root) return 'apex';
  if (full.endsWith(`.${root}`)) return full.slice(0, -(root.length + 1));
  return full;
};

const providerColor = (provider) => PROVIDER_COLORS[String(provider || '').toUpperCase()] || '#8392a5';
const rootColor = (index) => ROOT_COLORS[index % ROOT_COLORS.length];

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(String(value).length <= 10 ? `${value}T00:00:00` : String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: '2-digit' });
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(String(value).length <= 10 ? `${value}T00:00:00` : String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? null : date;
};

const daysLeft = (value) => {
  const date = parseDate(value);
  if (!date) return null;
  return Math.ceil((date.getTime() - Date.now()) / 86400000);
};

const serverStatus = (server) => {
  if (server.error) return 'error';
  const last = parseDate(server.fechaupdatedsincro);
  if (!last) return 'unknown';
  const minutes = (Date.now() - last.getTime()) / 60000;
  if (minutes > 30) return 'warning';
  return 'ok';
};

const curve = (source, target) => {
  const sx = source.x + source.w;
  const sy = source.y + source.h / 2;
  const tx = target.x;
  const ty = target.y + target.h / 2;
  const dx = Math.max(50, (tx - sx) / 2);
  return `M ${sx} ${sy} C ${sx + dx} ${sy}, ${tx - dx} ${ty}, ${tx} ${ty}`;
};

const MapaInfraestructura = () => {
  const [rawServers, setRawServers] = useState([]);
  const [rawDomains, setRawDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [showBackups, setShowBackups] = useState(true);
  const [zoom, setZoom] = useState(0.9);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [serversRes, domainsRes] = await Promise.all([
        api.get('/list/estadoservidores'),
        api.get('/list/dominios'),
      ]);
      setRawServers(serversRes.data?.estadoservidores || serversRes.data?.data || []);
      setRawDomains(domainsRes.data?.dominios || []);
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudo cargar la infraestructura.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const servers = useMemo(() => (rawServers || []).map((server) => {
    const domains = parseJsonArray(server.dominios).map(normalizeDomain).filter(Boolean);
    return {
      ...server,
      id: Number(server.id),
      ip: String(server.ip || '').trim(),
      provider: String(server.proveedor || 'Otros').trim(),
      location: String(server.ubicacion || '').trim(),
      domains,
      totalDomains: Number(server.total_dominios) || domains.length,
      status: serverStatus(server),
      backupDate: parseDate(server.fechabackup),
      lastSync: parseDate(server.fechaupdatedsincro),
    };
  }), [rawServers]);

  const serverById = useMemo(() => new Map(servers.map((server) => [server.id, server])), [servers]);
  const serverByIP = useMemo(() => new Map(servers.map((server) => [server.ip, server])), [servers]);

  const knownRoots = useMemo(() => (rawDomains || []).map((item) => normalizeDomain(item.dominio)).filter(Boolean), [rawDomains]);

  const roots = useMemo(() => {
    const map = new Map();
    const ensure = (name, explicit, order) => {
      if (!map.has(name)) {
        map.set(name, {
          name,
          registrador: explicit?.registrador || '',
          expiry: explicit?.fecha_vencimiento || null,
          days: explicit?.dias_restantes ?? daysLeft(explicit?.fecha_vencimiento),
          explicit: Boolean(explicit),
          order: explicit ? order : 9999,
          servers: new Set(),
          subMap: new Map(),
        });
      }
      return map.get(name);
    };

    (rawDomains || []).forEach((domain, index) => {
      const name = normalizeDomain(domain.dominio);
      if (!name) return;
      const root = ensure(name, domain, index);
      root.registrador = domain.registrador || root.registrador;
      root.expiry = domain.fecha_vencimiento || root.expiry;
      root.days = domain.dias_restantes ?? root.days;
    });

    servers.forEach((server) => {
      server.domains.forEach((domain) => {
        const rootName = rootOf(domain, knownRoots);
        if (!rootName) return;
        const root = ensure(rootName, null, 9999);
        root.servers.add(server.id);
        if (domain !== rootName) {
          if (!root.subMap.has(domain)) {
            root.subMap.set(domain, { full: domain, label: subdomainLabel(domain, rootName), servers: new Set() });
          }
          root.subMap.get(domain).servers.add(server.id);
        }
      });
    });

    (rawDomains || []).forEach((domain, index) => {
      const name = normalizeDomain(domain.dominio);
      if (!name) return;
      const root = ensure(name, domain, index);
      parseList(domain.servidores).forEach((ip) => {
        const server = serverByIP.get(ip);
        if (server) root.servers.add(server.id);
      });
      parseList(domain.subdominios).forEach((subdomain) => {
        const clean = normalizeDomain(subdomain);
        if (!clean) return;
        const full = clean.includes('.') ? clean : `${clean}.${name}`;
        if (!root.subMap.has(full)) {
          root.subMap.set(full, { full, label: subdomainLabel(full, name), servers: new Set() });
        }
      });
    });

    return [...map.values()]
      .map((root) => ({
        ...root,
        servers: [...root.servers].sort((a, b) => a - b),
        subdominios: [...root.subMap.values()]
          .map((sub) => ({ ...sub, servers: [...sub.servers].sort((a, b) => a - b) }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .filter((root) => root.servers.length || root.subdominios.length)
      .sort((a, b) => (a.explicit ? 0 : 1) - (b.explicit ? 0 : 1) || a.order - b.order || b.subdominios.length - a.subdominios.length);
  }, [rawDomains, servers, serverByIP, knownRoots]);

  const domainEdges = useMemo(() => {
    const edges = [];
    const seen = new Set();
    roots.forEach((root) => {
      root.servers.forEach((serverId) => {
        const server = serverById.get(serverId);
        if (!server) return;
        const key = `${serverId}-${root.name}`;
        if (seen.has(key)) return;
        seen.add(key);
        const count = server.domains.filter((domain) => rootOf(domain, knownRoots) === root.name).length;
        edges.push({ key, serverId, root: root.name, count });
      });
    });
    return edges;
  }, [roots, serverById, knownRoots]);

  const relations = useMemo(() => RELATIONS
    .map((relation) => {
      const fromServer = serverById.get(relation.from);
      const toServer = serverById.get(relation.to);
      return fromServer && toServer ? { ...relation, fromServer, toServer } : null;
    })
    .filter(Boolean), [serverById]);

  const backupTargets = useMemo(() => [12, 21, 22, 26]
    .map((id) => {
      const server = serverById.get(id);
      if (!server) return null;
      return {
        id,
        server,
        label: BACKUP_LABELS[id] || server.nombre,
        incoming: relations.filter((relation) => relation.to === id),
      };
    })
    .filter(Boolean), [serverById, relations]);

  const providers = useMemo(() => [...new Set(servers.map((server) => server.provider).filter(Boolean))].sort(), [servers]);

  const sortedServers = useMemo(() => [...servers].sort((a, b) => {
    const providerCompare = a.provider.localeCompare(b.provider);
    return providerCompare || a.nombre.localeCompare(b.nombre);
  }), [servers]);

  const layout = useMemo(() => {
    const serverX = 18;
    const serverW = 300;
    const serverH = 68;
    const serverGap = 10;
    const rootX = 430;
    const rootW = 440;
    const rootH = 128;
    const rootGap = 14;
    const backupX = 950;
    const backupW = 300;
    const backupH = 70;
    const backupGap = 12;
    const startY = 56;

    const serverPos = new Map();
    sortedServers.forEach((server, index) => {
      serverPos.set(server.id, { x: serverX, y: startY + index * (serverH + serverGap), w: serverW, h: serverH });
    });

    const rootPos = new Map();
    roots.forEach((root, index) => {
      rootPos.set(root.name, { x: rootX, y: startY + index * (rootH + rootGap), w: rootW, h: rootH });
    });

    const backupPos = new Map();
    backupTargets.forEach((backup, index) => {
      backupPos.set(backup.id, { x: backupX, y: startY + index * (backupH + backupGap), w: backupW, h: backupH });
    });

    const height = Math.max(
      startY + sortedServers.length * (serverH + serverGap) + 30,
      startY + roots.length * (rootH + rootGap) + 30,
      startY + backupTargets.length * (backupH + backupGap) + 30,
      620,
    );

    return { width: 1280, height, serverPos, rootPos, backupPos };
  }, [sortedServers, roots, backupTargets]);

  const domainPaths = useMemo(() => domainEdges
    .map((edge) => {
      const source = layout.serverPos.get(edge.serverId);
      const target = layout.rootPos.get(edge.root);
      return source && target ? { ...edge, d: curve(source, target) } : null;
    })
    .filter(Boolean), [domainEdges, layout]);

  const relationPaths = useMemo(() => relations
    .map((relation, index) => {
      const source = layout.serverPos.get(relation.from);
      const target = layout.backupPos.get(relation.to);
      return source && target ? { ...relation, key: `${relation.from}-${relation.to}-${index}`, d: curve(source, target) } : null;
    })
    .filter(Boolean), [relations, layout]);

  const queryLower = query.trim().toLowerCase();
  const serverMatches = useCallback((server) => {
    if (queryLower && !`${server.nombre} ${server.ip} ${server.provider} ${server.location} ${server.domains.join(' ')}`.toLowerCase().includes(queryLower)) return false;
    if (providerFilter !== 'all' && server.provider !== providerFilter) return false;
    return true;
  }, [queryLower, providerFilter]);

  const rootMatches = useCallback((root) => {
    if (!queryLower) return true;
    return `${root.name} ${root.registrador} ${root.subdominios.map((sub) => sub.full).join(' ')}`.toLowerCase().includes(queryLower);
  }, [queryLower]);

  const backupMatches = useCallback((backup) => {
    if (!queryLower) return true;
    return `${backup.label} ${backup.server.nombre} ${backup.server.ip}`.toLowerCase().includes(queryLower);
  }, [queryLower]);

  const isEdgeDim = (edge) => {
    const server = serverById.get(edge.serverId);
    const root = roots.find((item) => item.name === edge.root);
    return !server || !root || !serverMatches(server) || !rootMatches(root);
  };

  const isRelationDim = (relation) => {
    const source = serverById.get(relation.from);
    const target = serverById.get(relation.to);
    return !source || !target || !serverMatches(source) || !serverMatches(target);
  };

  const edgeClass = (edge) => {
    const active = (selected?.type === 'server' && selected.id === edge.serverId)
      || (selected?.type === 'root' && selected.name === edge.root);
    return `mapa-edge${isEdgeDim(edge) ? ' dim' : ''}${active ? ' highlight' : ''}`;
  };

  const relationClass = (relation) => {
    const active = (selected?.type === 'server' && (selected.id === relation.from || selected.id === relation.to))
      || (selected?.type === 'backup' && selected.id === relation.to);
    return `mapa-edge relation${isRelationDim(relation) ? ' dim' : ''}${active ? ' highlight' : ''}`;
  };

  const selectedServer = selected?.type === 'server' ? serverById.get(selected.id) : null;
  const selectedRoot = selected?.type === 'root' ? roots.find((root) => root.name === selected.name) : null;
  const selectedBackup = selected?.type === 'backup' ? backupTargets.find((backup) => backup.id === selected.id) : null;

  const totalSubdomains = roots.reduce((total, root) => total + root.subdominios.length, 0);
  const reportingServers = servers.filter((server) => server.status === 'ok').length;
  const selectedTitle = selectedServer?.nombre || selectedRoot?.name || selectedBackup?.label;

  return (
    <div className="page-container fade-in mapa-infra-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <ApartmentOutlined style={{ color: '#0168fa', marginRight: 8 }} />
            Mapa de infraestructura
          </h1>
          <p className="page-subtitle">
            Servidores, dominios raíz, subdominios y relaciones de failover / réplica / backup.
          </p>
        </div>
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

      <div className="mapa-infra-stats">
        <div className="mapa-stat-card">
          <div className="mapa-stat-label">Servidores</div>
          <div className="mapa-stat-value">{servers.length}</div>
          <div className="mapa-stat-hint">{reportingServers} reportando en los últimos 30 min</div>
        </div>
        <div className="mapa-stat-card">
          <div className="mapa-stat-label">Dominios raíz</div>
          <div className="mapa-stat-value">{roots.length}</div>
          <div className="mapa-stat-hint">Registrados y derivados</div>
        </div>
        <div className="mapa-stat-card">
          <div className="mapa-stat-label">Subdominios</div>
          <div className="mapa-stat-value">{totalSubdomains}</div>
          <div className="mapa-stat-hint">Apunados a servidores conocidos</div>
        </div>
        <div className="mapa-stat-card">
          <div className="mapa-stat-label">Relaciones</div>
          <div className="mapa-stat-value">{relations.length}</div>
          <div className="mapa-stat-hint">Failover, réplica y backup</div>
        </div>
      </div>

      <div className="mapa-infra-toolbar">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Buscar servidor, IP, dominio o subdominio"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Select
          value={providerFilter}
          onChange={setProviderFilter}
          style={{ width: 190 }}
          options={[{ value: 'all', label: 'Todos los proveedores' }, ...providers.map((provider) => ({ value: provider, label: provider }))]}
        />
        <Switch checked={showBackups} onChange={setShowBackups} checkedChildren="Backups" unCheckedChildren="Backups" />
        <Button icon={<ZoomOutOutlined />} onClick={() => setZoom((value) => Math.max(0.55, Number((value - 0.1).toFixed(2))))} />
        <Button icon={<ZoomInOutlined />} onClick={() => setZoom((value) => Math.min(1.4, Number((value + 0.1).toFixed(2))))} />
        <Button icon={<ReloadOutlined />} onClick={load}>Refrescar</Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>
      ) : !servers.length ? (
        <Empty className="mapa-empty" description="No hay servidores para graficar" />
      ) : (
        <>
          <div className="mapa-viewport">
            <div style={{ width: layout.width * zoom, height: layout.height * zoom, position: 'relative' }}>
              <div className="mapa-canvas" style={{ width: layout.width, height: layout.height, transform: `scale(${zoom})` }}>
                <div className="mapa-column-label" style={{ left: 18 }}><span>Servidores</span></div>
                <div className="mapa-column-label" style={{ left: 430 }}><span>Dominios raíz y subdominios</span></div>
                <div className="mapa-column-label" style={{ left: 950 }}><span>Failover / réplica / backup</span></div>

                <svg className="mapa-svg" width={layout.width} height={layout.height} aria-hidden="true">
                  <defs>
                    <marker id="relation-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                      <path d="M0,0 L8,4 L0,8 z" fill="#f0a33a" />
                    </marker>
                  </defs>
                  {domainPaths.map((edge) => (
                    <path key={edge.key} d={edge.d} className={edgeClass(edge)}>
                      <title>{`${serverById.get(edge.serverId)?.nombre} → ${edge.root} (${edge.count} dominios)`}</title>
                    </path>
                  ))}
                  {showBackups && relationPaths.map((relation) => (
                    <path key={relation.key} d={relation.d} className={relationClass(relation)} markerEnd="url(#relation-arrow)">
                      <title>{`${relation.fromServer.nombre} → ${relation.toServer.nombre}: ${relation.label}`}</title>
                    </path>
                  ))}
                </svg>

                {sortedServers.map((server) => {
                  const position = layout.serverPos.get(server.id);
                  const color = providerColor(server.provider);
                  const dim = !serverMatches(server);
                  return (
                    <div
                      key={server.id}
                      className={`mapa-node server${selected?.type === 'server' && selected.id === server.id ? ' selected' : ''}${dim ? ' dim' : ''}`}
                      style={{ left: position.x, top: position.y, width: position.w, height: position.h, '--provider-color': color, '--node-color': color }}
                      onClick={() => setSelected({ type: 'server', id: server.id })}
                      title={`${server.nombre} · ${server.ip}`}
                    >
                      <div className="mapa-node-title"><CloudServerOutlined /><span>{server.nombre}</span></div>
                      <div className="mapa-node-sub">{server.ip}{server.location ? ` · ${server.location}` : ''}</div>
                      <div className="mapa-node-meta">
                        <span className={`mapa-dot ${server.status}`} />
                        <span>{server.provider}</span>
                        <span>·</span>
                        <span>{server.totalDomains} dominios</span>
                        {server.backupDate && <span className="mapa-badge ok"><HddOutlined /> backup</span>}
                      </div>
                    </div>
                  );
                })}

                {roots.map((root, index) => {
                  const position = layout.rootPos.get(root.name);
                  const color = rootColor(index);
                  const dim = !rootMatches(root);
                  const visibleSubdomains = root.subdominios.slice(0, 5);
                  return (
                    <div
                      key={root.name}
                      className={`mapa-node root${selected?.type === 'root' && selected.name === root.name ? ' selected' : ''}${dim ? ' dim' : ''}`}
                      style={{ left: position.x, top: position.y, width: position.w, height: position.h, '--root-color': color, '--node-color': color }}
                      onClick={() => setSelected({ type: 'root', name: root.name })}
                      title={`${root.name} · ${root.servers.length} servidores · ${root.subdominios.length} subdominios`}
                    >
                      <div className="mapa-node-title"><GlobalOutlined /><span>{root.name}</span></div>
                      <div className="mapa-node-sub">
                        {root.registrador || 'Sin registrador'} · {root.expiry ? `vence ${formatDate(root.expiry)}` : 'sin fecha'}
                        {root.days !== null && root.days !== undefined ? ` (${root.days < 0 ? 'vencido' : `${root.days} días`})` : ''}
                      </div>
                      <div className="mapa-node-meta">
                        <span>{root.servers.length} servidores</span>
                        <span>·</span>
                        <span>{root.subdominios.length} subdominios</span>
                      </div>
                      <div className="mapa-chips">
                        {visibleSubdomains.map((sub) => (
                          <span className="mapa-chip" key={sub.full} title={sub.full}>{sub.label}</span>
                        ))}
                        {root.subdominios.length > visibleSubdomains.length && (
                          <span className="mapa-chip more">+{root.subdominios.length - visibleSubdomains.length}</span>
                        )}
                        {!root.subdominios.length && <span className="mapa-chip">sin subdominios</span>}
                      </div>
                    </div>
                  );
                })}

                {showBackups && backupTargets.map((backup) => {
                  const position = layout.backupPos.get(backup.id);
                  const dim = !backupMatches(backup);
                  return (
                    <div
                      key={backup.id}
                      className={`mapa-node backup${selected?.type === 'backup' && selected.id === backup.id ? ' selected' : ''}${dim ? ' dim' : ''}`}
                      style={{ left: position.x, top: position.y, width: position.w, height: position.h, '--node-color': '#f0a33a' }}
                      onClick={() => setSelected({ type: 'backup', id: backup.id })}
                      title={`${backup.label} · ${backup.server.nombre} · ${backup.server.ip}`}
                    >
                      <div className="mapa-node-title"><HddOutlined /><span>{backup.label}</span></div>
                      <div className="mapa-node-sub">{backup.server.nombre} · {backup.server.ip}</div>
                      <div className="mapa-node-meta">
                        <DatabaseOutlined />
                        <span>{backup.incoming.length} relación(es)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mapa-legend">
            <span className="mapa-legend-item"><span className="mapa-legend-line" /> Dominio apuntado a servidor</span>
            <span className="mapa-legend-item"><span className="mapa-legend-line relation" /> Failover / réplica / backup</span>
            <span className="mapa-legend-item"><span className="mapa-legend-dot" style={{ background: '#10b759' }} /> Reportando</span>
            <span className="mapa-legend-item"><span className="mapa-legend-dot" style={{ background: '#fd7e14' }} /> Sin reporte &gt; 30 min</span>
            <span className="mapa-legend-item"><span className="mapa-legend-dot" style={{ background: '#8392a5' }} /> Sin dato</span>
          </div>
        </>
      )}

      <Drawer
        title={selectedTitle || 'Detalle'}
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        width={460}
        styles={{ body: { background: '#fbfcff' } }}
      >
        {selectedServer && (
          <div>
            <div className="mapa-detail-section">
              <Tag color={providerColor(selectedServer.provider)}>{selectedServer.provider}</Tag>
              <Tag color={selectedServer.status === 'ok' ? 'green' : selectedServer.status === 'error' ? 'red' : 'default'}>
                {selectedServer.status === 'ok' ? 'Reportando' : selectedServer.status === 'error' ? 'Con error' : selectedServer.status === 'warning' ? 'Sin reporte' : 'Sin dato'}
              </Tag>
            </div>
            <div className="mapa-detail-section">
              <div className="mapa-detail-row"><span>IP</span><strong>{selectedServer.ip}</strong></div>
              <div className="mapa-detail-row"><span>Ubicación</span><strong>{selectedServer.location || '—'}</strong></div>
              <div className="mapa-detail-row"><span>Dominios</span><strong>{selectedServer.totalDomains}</strong></div>
              <div className="mapa-detail-row"><span>Último reporte</span><strong>{selectedServer.lastSync ? selectedServer.lastSync.toLocaleString('es-CO') : '—'}</strong></div>
              <div className="mapa-detail-row"><span>Backup</span><strong>{selectedServer.backupDate ? formatDate(selectedServer.backupDate) : 'Sin dato'}</strong></div>
            </div>
            <div className="mapa-detail-section">
              <div className="mapa-detail-title">Dominios apuntados</div>
              {roots.filter((root) => root.servers.includes(selectedServer.id)).map((root) => {
                const hosted = selectedServer.domains.filter((domain) => rootOf(domain, knownRoots) === root.name);
                return (
                  <div key={root.name} style={{ marginBottom: 12 }}>
                    <Text strong>{root.name}</Text>
                    <div className="mapa-chips" style={{ maxHeight: 120, overflow: 'auto' }}>
                      {hosted.map((domain) => <span className="mapa-chip server-chip" key={domain}>{domain}</span>)}
                      {!hosted.length && <span className="mapa-chip">raíz</span>}
                    </div>
                  </div>
                );
              })}
              {!roots.some((root) => root.servers.includes(selectedServer.id)) && <Text type="secondary">Sin dominios registrados.</Text>}
            </div>
            <div className="mapa-detail-section">
              <div className="mapa-detail-title">Relaciones de respaldo</div>
              {relations.filter((relation) => relation.from === selectedServer.id || relation.to === selectedServer.id).map((relation) => (
                <div className="mapa-detail-row" key={`${relation.from}-${relation.to}`}>
                  <span>{relation.fromServer.nombre} → {relation.toServer.nombre}</span>
                  <strong>{relation.type}</strong>
                </div>
              ))}
              {!relations.some((relation) => relation.from === selectedServer.id || relation.to === selectedServer.id) && <Text type="secondary">Sin relación registrada.</Text>}
            </div>
          </div>
        )}

        {selectedRoot && (
          <div>
            <div className="mapa-detail-section">
              <div className="mapa-detail-row"><span>Registrador</span><strong>{selectedRoot.registrador || '—'}</strong></div>
              <div className="mapa-detail-row"><span>Vencimiento</span><strong>{formatDate(selectedRoot.expiry)}</strong></div>
              <div className="mapa-detail-row"><span>Días restantes</span><strong>{selectedRoot.days ?? '—'}</strong></div>
              <div className="mapa-detail-row"><span>Servidores</span><strong>{selectedRoot.servers.length}</strong></div>
              <div className="mapa-detail-row"><span>Subdominios</span><strong>{selectedRoot.subdominios.length}</strong></div>
            </div>
            <div className="mapa-detail-section">
              <div className="mapa-detail-title">Servidores que lo sirven</div>
              <div className="mapa-chips">
                {selectedRoot.servers.map((id) => {
                  const server = serverById.get(id);
                  return server ? <span className="mapa-chip server-chip" key={id}>{server.nombre} · {server.ip}</span> : null;
                })}
              </div>
            </div>
            <div className="mapa-detail-section">
              <div className="mapa-detail-title">Subdominios apuntados</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {selectedRoot.subdominios.map((sub) => (
                  <div className="mapa-detail-row" key={sub.full} title={sub.full}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub.full}</span>
                    <strong style={{ flex: '0 0 auto' }}>{sub.servers.length ? sub.servers.map((id) => serverById.get(id)?.nombre).filter(Boolean).join(', ') : '—'}</strong>
                  </div>
                ))}
                {!selectedRoot.subdominios.length && <Text type="secondary">Sin subdominios registrados.</Text>}
              </div>
            </div>
          </div>
        )}

        {selectedBackup && (
          <div>
            <div className="mapa-detail-section">
              <div className="mapa-detail-row"><span>Servidor destino</span><strong>{selectedBackup.server.nombre}</strong></div>
              <div className="mapa-detail-row"><span>IP</span><strong>{selectedBackup.server.ip}</strong></div>
              <div className="mapa-detail-row"><span>Proveedor</span><strong>{selectedBackup.server.provider}</strong></div>
              <div className="mapa-detail-row"><span>Relaciones entrantes</span><strong>{selectedBackup.incoming.length}</strong></div>
            </div>
            <div className="mapa-detail-section">
              <div className="mapa-detail-title">Origen de los respaldos</div>
              {selectedBackup.incoming.map((relation) => (
                <div className="mapa-detail-row" key={relation.from}>
                  <span>{relation.fromServer.nombre} ({relation.fromServer.ip})</span>
                  <strong>{relation.label}</strong>
                </div>
              ))}
              {!selectedBackup.incoming.length && <Text type="secondary">Sin relaciones entrantes registradas.</Text>}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default MapaInfraestructura;
